/* eslint-disable sort-keys-fix/sort-keys-fix -- CSS-in-JS pseudo-selector keys use semantic ordering */
// MUI v4 IconButton — NS/Tractie component overrides.
// Family-anatomy: Button-family (icon-only variant). IconButton is een Tractie-spirit
// equivalent van `<TractieButton iconOnly>`; we volgen daarom de Tractie-button-spec
// (geen ripple, instant transitions, opacity 0.25 voor disabled, focus-formule
// outline + offset + shadow op root).
//
// Tractie deviations from MUI defaults:
//  - geen ripple (globaal uit via MuiButtonBase.disableRipple in theme.ts)
//  - transition: none (Tractie state-changes zijn instant)
//  - disabled: opacity 0.25 (matcht TractieButton ghost-variant uit mui-button-overrides)
//  - focus-formule op root, identiek aan Button: outline + offset + shadow-elevated-1
//  - geen secondary color (MUI's pink/magenta neutraliseren naar default of primary)
//  - hover/active backgrounds via NS tokens i.p.v. alpha-blend van palette
//
// Force-class foundation (per packages/storybook-utils/src/comparison/force-state.ts):
// elke pseudo-state-rule heeft een comma-separated `&.force-*` variant zodat
// Storybook comparison-grids de state kunnen forceren zonder native pseudo-trigger.
//
// MUI v4 IconButton defaults (bron: @material-ui/core/IconButton/IconButton.js):
//   root.padding              → 12              behouden (40x40 touch target met 24px icon)
//   root.borderRadius         → '50%'           behouden (ronde ripple-area)
//   root.fontSize             → pxToRem(24)     behouden
//   root.color                → action.active   reset → var(--ns-color-fg-default)
//   root.transition           → background-color reset → 'none'
//   root.&:hover.bg           → alpha-blend     reset → var(--ns-color-bg-hover)
//   root.&$disabled.bg        → 'transparent'   behouden (we doen opacity i.p.v.)
//   root.&$disabled.color    → action.disabled  reset → opacity 0.25
//   edgeStart.marginLeft      → -12             behouden
//   edgeEnd.marginRight       → -12             behouden
//   sizeSmall.padding         → 3               behouden (28x28 touch target)
//   sizeSmall.fontSize        → pxToRem(18)     behouden
//   colorPrimary.color        → palette.primary reset → var(--ns-color-fg-interactive-default)
//   colorPrimary.&:hover.bg   → alpha-primary   reset → var(--ns-color-bg-hover)
//   colorSecondary.*          → pink            reset → alias colorPrimary (geen NS-secondary)
//
// Focus-formule (Tractie spec, identiek aan Button per mui-button-overrides):
//   outline:        var(--ns-border-width-medium) solid var(--ns-color-border-focus);
//   outline-offset: var(--ns-border-width-thin);
//   box-shadow:     var(--ns-shadow-elevated-1);

const focusVisibleOnRoot = {
  outline: 'var(--ns-border-width-medium) solid var(--ns-color-border-focus)',
  outlineOffset: 'var(--ns-border-width-thin)',
  boxShadow: 'var(--ns-shadow-elevated-1)',
} as const;

export const MuiIconButtonLightOverrides = {
  // Root: align to Tractie iconOnly geometrie — 40x40 rounded square @ 6px radius,
  // padding 7px om 24px icon. MUI v4 default is 48x48 padding 12 borderRadius 50%
  // (cirkel) → wij overschrijven naar Tractie button-radius-m + Tractie-spirit
  // padding voor cross-library pixel-pariteit.
  root: {
    width: 40,
    height: 40,
    padding: 7,
    borderRadius: 6,
    color: 'var(--ns-color-fg-default)',
    transition: 'none',
    '&:hover, &.force-hover': {
      backgroundColor: 'var(--ns-color-bg-hover)',
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
    '&:active, &.force-active': {
      backgroundColor: 'var(--ns-color-bg-pressed)',
    },
    '&.Mui-focusVisible, &.force-focus-visible': focusVisibleOnRoot,
    '&$disabled': {
      backgroundColor: 'transparent',
      color: 'var(--ns-color-fg-default)',
      opacity: 0.25,
    },
  },
  // colorPrimary: Tractie-spirit gevulde primary — filled NS interactive bg
  // met witte icon-color (matcht TractieButton level="primary" + BUI
  // variant="primary"). MUI v4 default colorPrimary kleurt alleen de
  // foreground; wij overschrijven naar filled-mode voor cross-library
  // visuele pariteit.
  colorPrimary: {
    backgroundColor: 'var(--ns-color-bg-interactive-default)',
    color: 'var(--ns-color-fg-default-inverse, #ffffff)',
    '&:hover, &.force-hover': {
      backgroundColor: 'var(--ns-color-bg-interactive-hover)',
      '@media (hover: none)': {
        backgroundColor: 'var(--ns-color-bg-interactive-default)',
      },
    },
    '&:active, &.force-active': {
      backgroundColor: 'var(--ns-color-bg-interactive-pressed)',
    },
  },
  // colorSecondary: alias naar primary (Tractie kent geen secondary-accent
  // voor icon-buttons, MUI's pink/magenta neutraliseren naar NS interactive).
  colorSecondary: {
    backgroundColor: 'var(--ns-color-bg-interactive-default)',
    color: 'var(--ns-color-fg-default-inverse, #ffffff)',
    '&:hover, &.force-hover': {
      backgroundColor: 'var(--ns-color-bg-interactive-hover)',
      '@media (hover: none)': {
        backgroundColor: 'var(--ns-color-bg-interactive-default)',
      },
    },
    '&:active, &.force-active': {
      backgroundColor: 'var(--ns-color-bg-interactive-pressed)',
    },
  },
  // sizeSmall: align to Tractie size="s" — 32x32 rounded square padding 3px
  // om 18px icon. MUI v4 default sizeSmall is 24x24 padding 3px → te klein vs
  // Tractie spec. Wij forceren NS-standaard touch-target 32x32.
  sizeSmall: {
    width: 32,
    height: 32,
    padding: 3,
    borderRadius: 6,
  },
  // edgeStart / edgeEnd: behoud MUI's negatieve margin (InputAdornment-positionering),
  // geen NS-deviatie nodig — positionering is pure layout-hulpmiddel.
} as const;

export const MuiIconButtonDarkOverrides = MuiIconButtonLightOverrides;
